import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CURRENCIES } from '../lib/currency';
import type { EventMeta, VatMode } from '../lib/types';
import CityAutocomplete from './CityAutocomplete';
import CountrySelect from './CountrySelect';

type Props = {
  meta: EventMeta;
  onChange: (patch: Partial<EventMeta>) => void;
  vatMode: VatMode;
  vatRate: number;
  onVatModeChange: (mode: VatMode) => void;
  onVatRateChange: (rate: number) => void;
  email?: string;
  saving: boolean;
  saved: boolean;
  onSave: () => void;
  onExportXlsx: () => void;
  onExportPptx: () => void;
  onExportPdf: () => void;
  onLogout: () => void;
};

export default function TopBar({
  meta,
  onChange,
  vatMode,
  vatRate,
  onVatModeChange,
  onVatRateChange,
  email,
  saving,
  saved,
  onSave,
  onExportXlsx,
  onExportPptx,
  onExportPdf,
  onLogout,
}: Props) {
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!exportOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!exportRef.current) return;
      if (!exportRef.current.contains(e.target as Node)) setExportOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setExportOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportOpen]);

  function pick(handler: () => void) {
    setExportOpen(false);
    handler();
  }

  return (
    <div className="topbar">
      <div className="topbar-row">
        <div className="topbar-app">
          <Link to="/" className="brand-link">
            <img className="brand-logo" src="/favicon.svg" alt="" />
            <span>← Revenue Estimator</span>
          </Link>
          <a
            className="kofi-mini"
            href="https://ko-fi.com/W4Z120VC3R"
            target="_blank"
            rel="noopener noreferrer"
            title="Buy me a coffee"
          >
            <img
              className="kofi-mini-img"
              src="https://storage.ko-fi.com/cdn/kofi4.png?v=6"
              alt="Buy me a coffee"
            />
          </a>
        </div>
        <div className="row center gap-10">
          <span className="muted">{email}</span>
          <button className="btn" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
          </button>
          <div className="export-menu" ref={exportRef}>
            <button
              className="btn ghost"
              onClick={() => setExportOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={exportOpen}
            >
              Export ▾
            </button>
            {exportOpen && (
              <div className="export-menu-popover" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="export-menu-item"
                  onClick={() => pick(onExportXlsx)}
                >
                  <span className="export-menu-item-title">Excel workbook</span>
                  <span className="export-menu-item-sub">
                    .xlsx · all data on 4 sheets
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="export-menu-item"
                  onClick={() => pick(onExportPptx)}
                >
                  <span className="export-menu-item-title">PowerPoint deck</span>
                  <span className="export-menu-item-sub">
                    .pptx · slides for sharing
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="export-menu-item"
                  onClick={() => pick(onExportPdf)}
                >
                  <span className="export-menu-item-title">PDF report</span>
                  <span className="export-menu-item-sub">
                    .pdf · printable summary
                  </span>
                </button>
              </div>
            )}
          </div>
          <button className="btn ghost" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </div>

      <div className="topbar-fields">
        <div className="field">
          <label>Event name</label>
          <input
            className="input"
            value={meta.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="My Conference 2027"
          />
        </div>
        <div className="field">
          <label>Country</label>
          <CountrySelect
            value={meta.country_code}
            onChange={(code) => onChange({ country_code: code })}
          />
        </div>
        <div className="field">
          <label>City</label>
          <CityAutocomplete
            value={meta.city}
            countryCode={meta.country_code}
            onChangeText={(city) => onChange({ city })}
            onPick={(city, code) =>
              onChange({ city, country_code: code || meta.country_code })
            }
          />
        </div>
        <div className="field">
          <label>Start date</label>
          <input
            className="input"
            type="date"
            value={meta.start_date ?? ''}
            onChange={(e) =>
              onChange({ start_date: e.target.value || null })
            }
          />
        </div>
        <div className="field">
          <label>End date</label>
          <input
            className="input"
            type="date"
            value={meta.end_date ?? ''}
            onChange={(e) => onChange({ end_date: e.target.value || null })}
          />
        </div>
        {meta.event_type !== 'meetup' && (
          <>
            <div className="field">
              <label>Conference days</label>
              <input
                className="input"
                type="number"
                min={0}
                value={meta.conference_days}
                onChange={(e) =>
                  onChange({ conference_days: Math.max(0, +e.target.value || 0) })
                }
              />
            </div>
            <div className="field">
              <label>Workshop days</label>
              <input
                className="input"
                type="number"
                min={0}
                value={meta.workshop_days}
                onChange={(e) =>
                  onChange({ workshop_days: Math.max(0, +e.target.value || 0) })
                }
              />
            </div>
          </>
        )}
        <div className="field">
          <label>Currency</label>
          <select
            className="input"
            value={meta.currency}
            onChange={(e) => onChange({ currency: e.target.value })}
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="vat-row">
        <span className="vat-label">Amounts:</span>
        <div className="vat-toggle" role="radiogroup" aria-label="VAT mode">
          <button
            type="button"
            role="radio"
            aria-checked={vatMode === 'net'}
            className={`vat-toggle-btn ${vatMode === 'net' ? 'active' : ''}`}
            onClick={() => onVatModeChange('net')}
          >
            Net
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={vatMode === 'gross'}
            className={`vat-toggle-btn ${vatMode === 'gross' ? 'active' : ''}`}
            onClick={() => onVatModeChange('gross')}
          >
            Gross
          </button>
        </div>
        <span className="vat-info" tabIndex={0} aria-label="VAT mode help">
          <span className="vat-info-icon">ⓘ</span>
          <span className="vat-info-popover" role="tooltip">
            <strong>Net (recommended for budgeting).</strong> Amounts you
            enter are simple net values. No VAT math, no extra columns —
            cleanest for forecasting and reverse-charge invoicing.
            <br />
            <br />
            <strong>Gross.</strong> Amounts you enter remain net, but the app
            applies a single VAT rate on top. Bars are split into a net
            portion + VAT portion, and totals show net · VAT · gross.
            <br />
            <br />
            The app uses one flat rate per event — for mixed-rate scenarios
            (some 23%, some reverse-charge) keep <em>Net</em> mode and reason
            in net throughout.
          </span>
        </span>
        {vatMode === 'gross' && (
          <span className="vat-rate">
            <span className="muted">VAT rate</span>
            <input
              type="number"
              className="input vat-rate-input"
              value={Math.round(vatRate * 1000) / 10}
              onChange={(e) => {
                const pct = Number(e.target.value);
                if (!Number.isFinite(pct)) return;
                onVatRateChange(Math.min(2, Math.max(0, pct / 100)));
              }}
              min={0}
              max={100}
              step={0.5}
              aria-label="VAT rate percent"
            />
            <span>%</span>
          </span>
        )}
        <span className="vat-current">
          {vatMode === 'net'
            ? <>All numbers on this event read as <strong>net of VAT</strong>.</>
            : <>Amounts entered are <strong>net</strong>; bars and totals add{' '}
                <strong>{(Math.round(vatRate * 1000) / 10).toFixed(1)}% VAT</strong>{' '}
                on top.</>
          }
        </span>
      </div>
    </div>
  );
}
